import { useEffect, useRef, useState, type ReactNode } from "react";
import { FooterSlotContext } from "./footerSlot";
import classes from "./AppWebviewLayout.module.css";

interface AppWebviewLayoutProps {
  children: ReactNode;
  footer?: ReactNode;
  fillBody?: boolean;
  noPadding?: boolean;
  /** Renders an empty footer bar for a child `FooterButtons` to portal into. */
  footerSlot?: boolean;
  /** Scrolls the body to the top when this changes. React reuses the scroll
   * container when only `children` swap, so it would keep the old offset. */
  scrollResetKey?: unknown;
}

export function AppWebviewLayout({
  children,
  footer,
  fillBody = false,
  noPadding = false,
  footerSlot = false,
  scrollResetKey,
}: AppWebviewLayoutProps) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // scrollTop over scrollTo: jsdom leaves Element.scrollTo undefined.
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  const bodyClass = fillBody
    ? classes.bodyFill
    : noPadding
      ? classes.bodyNoPadding
      : classes.body;

  return (
    <FooterSlotContext.Provider value={slot}>
      <div className={classes.root}>
        <main ref={bodyRef} className={bodyClass}>
          {children}
        </main>
        {footer && <div className={classes.footer}>{footer}</div>}
        {footerSlot && <div className={classes.footer} ref={setSlot} />}
      </div>
    </FooterSlotContext.Provider>
  );
}
